export interface GuardrailResult {
  passed: boolean;
  flags: string[];
  sanitizedPrompt?: string;
}

const PROMPT_INJECTION_PATTERNS = [
  /ignore (previous|all) instructions/i,
  /forget (previous|all) instructions/i,
  /you are now/i,
  /new instructions:/i,
  /disregard (previous|all)/i,
  /override (previous|all)/i,
  /system prompt:/i,
  /admin mode/i,
  /developer mode/i,
  /jailbreak/i,
];

const SENSITIVE_DATA_PATTERNS = [
  /\b\d{3}-\d{2}-\d{4}\b/,
  /\b\d{16}\b/,
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i,
  /\b(?:password|passwd|pwd|secret|token|api[_-]?key)\s*[:=]\s*\S+/i,
];

const FORBIDDEN_CONTENT = [
  /\b(hack|exploit|breach|penetrate|vulnerability)\b/i,
  /\b(sql injection|xss|csrf)\b/i,
  /\b(credit card|ssn|social security)\b/i,
];

const SQL_INJECTION_PATTERNS = [
  /(\bor\b|\band\b)\s+\d+\s*=\s*\d+/i,
  /union\s+select/i,
  /drop\s+table/i,
  /delete\s+from/i,
  /insert\s+into/i,
  /update\s+.*\s+set/i,
];

export function applyGuardrails(prompt: string, context?: Record<string, any>): GuardrailResult {
  const flags: string[] = [];

  if (detectPromptInjection(prompt)) {
    flags.push('prompt_injection');
  }

  if (detectSensitiveData(prompt)) {
    flags.push('sensitive_data');
  }

  if (detectForbiddenContent(prompt)) {
    flags.push('forbidden_content');
  }

  if (detectSQLInjection(prompt)) {
    flags.push('sql_injection');
  }

  if (prompt.length > 10000) {
    flags.push('excessive_length');
  }

  if (context && detectDataLeakage(context)) {
    flags.push('potential_data_leakage');
  }

  const passed = flags.length === 0;

  const sanitizedPrompt = sanitizePrompt(prompt);

  return {
    passed,
    flags,
    sanitizedPrompt,
  };
}

function detectPromptInjection(prompt: string): boolean {
  return PROMPT_INJECTION_PATTERNS.some(pattern => pattern.test(prompt));
}

function detectSensitiveData(prompt: string): boolean {
  return SENSITIVE_DATA_PATTERNS.some(pattern => pattern.test(prompt));
}

function detectForbiddenContent(prompt: string): boolean {
  return FORBIDDEN_CONTENT.some(pattern => pattern.test(prompt));
}

function detectSQLInjection(prompt: string): boolean {
  return SQL_INJECTION_PATTERNS.some(pattern => pattern.test(prompt));
}

function detectDataLeakage(context: Record<string, any>): boolean {
  const contextString = JSON.stringify(context);

  if (contextString.includes('password') || contextString.includes('api_key')) {
    return true;
  }

  if (SENSITIVE_DATA_PATTERNS.some(pattern => pattern.test(contextString))) {
    return true;
  }

  return false;
}

function sanitizePrompt(prompt: string): string {
  let sanitized = prompt;

  sanitized = sanitized.replace(/[<>]/g, '');

  sanitized = sanitized.replace(/\b(password|api[_-]?key|secret|token)\s*[:=]\s*\S+/gi, '$1: [REDACTED]');

  sanitized = sanitized.replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN_REDACTED]');
  sanitized = sanitized.replace(/\b\d{16}\b/g, '[CARD_REDACTED]');

  return sanitized.trim();
}

export function validateAIResponse(response: string): GuardrailResult {
  const flags: string[] = [];

  if (detectSensitiveData(response)) {
    flags.push('sensitive_data_in_response');
  }

  if (response.includes('```sql') || response.includes('DROP TABLE') || response.includes('DELETE FROM')) {
    flags.push('potentially_dangerous_code');
  }

  if (response.length > 5000) {
    flags.push('excessive_response_length');
  }

  return {
    passed: flags.length === 0,
    flags,
    sanitizedPrompt: sanitizePrompt(response),
  };
}
